import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { FcGoogle } from "react-icons/fc";
import { ArrowRight } from "lucide-react";
import { API_BASE_URL } from "@/utils/api";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const togglePassword = () => setShowPassword(!showPassword);

  const handleSuccessLogin = (userData: any, token: string) => {
    const userToSave = {
      ...userData,
      role: userData.role || "student",
    };

    localStorage.setItem("user", JSON.stringify(userToSave));
    if (token) localStorage.setItem("token", token);

    window.dispatchEvent(new Event("storage"));

    if (userToSave.role === "admin") {
      alert(`Selamat Datang Administrator, ${userData.name}!`);
      navigate("/admin/overview");
    } else {
      alert(`Selamat Datang, ${userData.name}!`);
      navigate("/");
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const payload = {
        email,
        password,
      };

      const response = await fetch(
        `${API_BASE_URL}/auth/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      console.log(`📥 Login response status: ${response.status}`);

      console.log("📥 Login response status:", response.status);

      const data = await response.json();

      if (response.ok && data.success) {
        console.log("✅ Login successful:", data.user);
        handleSuccessLogin(data.user, data.token);
      } else {
        console.error("❌ Login failed:", data.message);
        alert(data.message || "Login Gagal. Periksa email dan password Anda.");
      }
    } catch (error: any) {
      console.error("❌ Error:", error);
      if (
        error.name === "TypeError" &&
        error.message.includes("Failed to fetch")
      ) {
        alert(
          "Tidak dapat terhubung ke server. Pastikan backend berjalan di port 5000",
        );
      } else {
        alert("Terjadi kesalahan. Silakan coba lagi.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        console.log("🔑 Google token received");

        const userInfoRes = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
          },
        );

        if (!userInfoRes.ok) {
          throw new Error("Gagal mendapatkan info dari Google");
        }

        const googleUser = await userInfoRes.json();
        console.log("👤 Google user:", googleUser.email);

        const response = await fetch(
          `${API_BASE_URL}/auth/google`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              email: googleUser.email,
              name: googleUser.name,
              token: tokenResponse.access_token,
            }),
          },
        );

        const data = await response.json();

        if (response.ok && data.success) {
          console.log("✅ Google login successful:", data.message);
          handleSuccessLogin(data.user, data.token);
        } else {
          throw new Error(data.message || "Terjadi kesalahan pada server");
        }
      } catch (error: any) {
        console.error("❌ Google login error:", error);
        alert("Error Google Login: " + error.message);
      }
    },
    onError: (error) => {
      console.error("❌ Google login failed:", error);
      alert("Google login failed");
    },
  });

  const responseFacebook = async (response: any) => {
    console.log("🔑 Facebook response:", response);

    if (response.accessToken) {
      try {
        // Gunakan proxy untuk Facebook login
        const res = await fetch(
          `${API_BASE_URL}/auth/facebook`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              accessToken: response.accessToken,
              userID: response.userID,
              email: response.email,
              name: response.name,
              picture: response.picture?.data?.url,
            }),
          },
        );

        const data = await res.json();

        if (res.ok && data.success) {
          console.log("✅ Facebook login successful");
          handleSuccessLogin(data.user, data.token);
        } else {
          alert("Gagal Login Facebook: " + (data.message || "Unknown error"));
        }
      } catch (error: any) {
        console.error("❌ Facebook login error:", error);
        alert("Error FB Login: " + error.message);
      }
    } else {
      console.error("❌ Facebook no access token");
      alert("Facebook login canceled or failed");
    }
  };

  return (
    <>
      <style>{`
        :root {
            --primary-red: #B70100;
            --admin-dark: #1F2937;
            --text-dark: #111827;
            --text-gray: #E5E7EB;
            --border-color: rgba(255, 255, 255, 0.16);
            --glass-bg: rgba(255, 255, 255, 0.1);
            --bg-page: #F3F4F6;
        }

        body {
            min-height: 100vh;
            background-color: var(--bg-page);
            font-family: 'Poppins', sans-serif;
            margin: 0;
            padding: 0;
        }

        .auth-page {
            min-height: 100vh;
            display: flex;
            align-items: stretch;
            box-sizing: border-box;
        }

        .auth-layout {
            width: 100%;
            display: flex;
            align-items: stretch;
        }

        .left-panel {
            flex: 1.3;
            background: #B70100;
            display: flex;
            flex-direction: column;
            justify-content: center;
            box-shadow: 0 24px 80px rgba(183, 1, 0, 0.25);
            overflow: hidden;
        }

        .left-panel::before {
            content: '';
            position: absolute;
            inset: 0;
            background: radial-gradient(circle at top left, rgba(255,255,255,0.18), transparent 25%),
                        radial-gradient(circle at bottom right, rgba(255,255,255,0.12), transparent 20%);
            pointer-events: none;
            opacity: 0.7;
        }

        .left-content {
            position: relative;
            z-index: 1;
            display: grid;
            gap: 24px;
            margin-top: 130px;
            margin-bottom: 100px;
        }

        .left-content img {
            opacity: 0.10;
        }

        .glass-card {
            width: 100%;
            background: var(--glass-bg);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 32px;
            color: white;
            padding: 28px 28px;
            display: flex;
            flex-direction: column;
            gap: 14px;
        }

        .glass-card.top-card {
            padding: 10px 24px;
            border-radius: 999px;
            align-items: flex-start;
            justify-content: center;
        }

        .glass-card .label-pill {
            display: inline-flex;
            align-items: center;
            gap: 12px;
            font-size: 14px;
            font-weight: 500;
            letter-spacing: 0.02em;
            color: #FFFFFF;
        }

        .glass-card .label-pill::before {
            content: '';
            width: 14px;
            height: 14px;
            border-radius: 999px;
            background: #35C759;
            box-shadow: 0 0 0 6px rgba(53, 199, 89, 0.16);
            display: inline-block;
        }

        .glass-card.hero-card {
            min-height: 100px;
            justify-content: center;
            backdrop-filter: blur(24px);
            -webkit-backdrop-filter: blur(24px);
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
        }

        .hero-card h2 {
            margin: 0;
            font-size: 0.95rem;
            line-height: 0.5;
            font-weight: 300;
            color: #FFFFFF;
        }
            
        .hero-card h3 {
          margin: 0;
          font-size: 0.95rem;
          line-height: 0.5;
          font-weight: 300;
          color: #FFFFFF;
        }

        .hero-card p {
            margin: 0;
            color: rgba(255, 255, 255, 0.8);
            font-size: 0.95rem;
            line-height: 1.7;
            max-width: 420px;
            font-weight: 300;
        }

        .login-panel {
            flex: 0.9;
            min-width: 320px;
            display: flex;
        }

        .login-container {
            width: 100%;
            background: #ffffff;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
            position: relative;
            transition: all 0.3s ease;
        }

        .login-container.admin-mode {
            border-top: 5px solid var(--admin-dark);
        }

        .top-nav { margin-bottom: 20px; }
        .back-link {
            text-decoration: none;
            color: var(--text-dark);
            font-weight: 600;
            font-size: 14px;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            transition: color 0.2s;
        }
        .back-link:hover { color: var(--primary-red); }

        .form-group { margin-bottom: 20px; }
        .form-label { display: block; font-size: 14px; font-weight: 600; color: rgba(17, 24, 39, 0.8); margin-bottom: 6px; margin-left: 4px; }
        .input-wrapper { position: relative; }
        .input-icon { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: #946E68; font-size: 15px; }
        .form-input {
            width: 100%; padding: 14px 16px 14px 44px; border: 1px solid #E9BCB5;
            border-radius: 12px; font-size: 14px; color: #111827;
            box-sizing: border-box; outline: none; transition: all 0.2s;
            background: #FCFCFC;
        }
        .form-input:focus { border-color: var(--primary-red); box-shadow: 0 0 0 2px rgba(183, 1, 0, 0.12); }
        .toggle-pass { position: absolute; right: 16px; top: 50%; transform: translateY(-50%); color: #9CA3AF; cursor: pointer; font-size: 16px; }

        .remember-box { display: flex; align-items: center; gap: 8px; color: #6B7280; cursor: pointer; padding-left: 4px; margin-top: 8px; }
        .remember-box input { width: 16px; height: 16px; accent-color: var(--primary-red); border: 1px solid #D1D5DB; border-radius: 4px; cursor: pointer; }
        .forgot-link { color: var(--primary-red); text-decoration: none; font-weight: 600; margin-top: -2px; }
        .forgot-link:hover { text-decoration: underline; }

        .btn-login {
            width: 100%;
            background-color: var(--primary-red);
            color: white; border: none;
            padding: 14px; border-radius: 12px; font-size: 14px; font-weight: 600;
            cursor: pointer; transition: all 0.2s; margin-bottom: 5px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            box-shadow: 0 14px 30px rgba(183, 1, 0, 0.22);
        }
        .admin-mode .btn-login { background-color: var(--admin-dark); }
        
        .btn-login:hover { opacity: 0.9; }
        .btn-login:disabled { opacity: 0.7; cursor: not-allowed; }

        .social-grid { display: flex; flex-direction: column; gap: 15px; width: 100%; }
        .btn-social {
            display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%;
            padding: 12px; background: white; border-radius: 8px; cursor: pointer;
            font-size: 14px; font-weight: 600; transition: all 0.2s; text-decoration: none;
            border: 1px solid var(--border-color);
            color: #111827;
        }
        .btn-social svg { width: 20px; height: 20px; }
        .btn-social.google { border-color: #FF0000; }
        .btn-social.google svg { color: #FF0000; }
        .btn-social.facebook { border-color: #FF0000; }
        .btn-social.facebook svg { color: #1877F2; }
        .btn-social:hover { 
            background-color: #FAFAFA; 
            transform: translateY(-1px);
            box-shadow: 0 2px 4px var(--primary-red);
        }

            .footer-text { text-align: center; font-size: 14px; color: #6B7280; }
            .signup-link { display: flex; gap: 4px; color: var(--primary-red); font-weight: 600; text-decoration: none; margin-left: 5px; font-size: 14px; align-items: center;}
            .signup-link:hover { text-decoration: underline; }

            .admin-toggle {
                display: flex;
                margin-top: 30px;
                text-align: center;
                border-top: 1px solid #E5E7EB;
                padding-top: 20px;
                justify-content: center;
            }
            .btn-admin-toggle {
                background: none; border: none; color: #6B7280;
                font-size: 14px; cursor: pointer; text-decoration: underline;
                padding: 8px 5px; font-weight: 500;
            }
            .btn-admin-toggle:hover { color: var(--text-dark); }

            .actions-links { font-size: 13px; }

            .loading-spinner {
                width: 16px;
                height: 16px;
                border: 2px solid rgba(255, 255, 255, 0.3);
                border-radius: 50%;
                border-top-color: white;
                animation: spin 1s ease-in-out infinite;
            }

            .login-desc-label {
                width: 35%;
                text-align: center;
                font-size: 0.75rem;
                color: rgba(91, 95, 99, 0.6);
                margin: 0 auto;
            }

            @keyframes spin {
                to { transform: rotate(360deg); }
            }

        @media (max-width: 900px) {
          .auth-layout { flex-direction: column; gap: 24px; }
          .left-panel, .login-panel { width: 100%; height: auto; }
          .left-panel { padding: 28px 24px; }
          .left-content { margin-top: 0; }
          .glass-card { padding: 22px 20px; }
          .glass-card.top-card { padding: 10px 18px; }
          .login-panel { padding: 25px; }
          .login-container { max-width: 100%; padding: 28px 24px; border-radius: 32px;}
          .left-content {margin: 0;}
          .left-content img {display: none;}
          .img-con {display: none;}
        }

        @media (max-width: 760px) {
            .form-row { flex-direction: column; }
            .form-row .input-wrapper { min-width: 100%; }
            .input-field { padding: 12px 14px; }
            .btn-create { padding: 12px; font-size: 13px; }
            .header-title h2 { font-size: 24px; }
            .header-title p { font-size: 13px; }
            .glass-card.hero-card { min-height: 140px; }
            .left-content {margin: 0;}
            .left-content img {display: none;}
            .img-con {display: none;}
            }
            
          @media (max-width: 520px) {
            // .left-content {margin: 0;}
            .left-panel { display: none; }
            .login-panel { padding: 20px; }
            .login-container { border-radius: 24px; padding: 22px 22px; }
            .social-buttons { gap: 10px; }
            .divider { margin: 10px 0; }
            .header-title { margin-bottom: 18px; }
        }
      `}</style>

      <div className="auth-page">
        <div className="auth-layout">
          <aside className="left-panel px-10">
            <div className="left-content">
              <img
                src="/Login/Sign-in Icon 1.svg"
                alt="icon 1"
                className="w-28 h-32 -mt-48"
              />
              <div className="glass-card top-card">
                <span className="label-pill">TOP RATED BIPA PROVIDER 2024</span>
              </div>

              <div className="glass-card hero-card">
                <h2>Mulai Belajar</h2>
                <h3>
                  <i className="text-[#FFFFFF]/70">Bahasa</i> Indonesia
                </h3>
                <p>
                  Join 25,000+ global students mastering Indonesian with
                  Fluenesia. Native tutors & immersive cultural curriculum.
                </p>
              </div>
            </div>
            <div className="img-con flex flex-col pl-20">
              <div className="w-full justify-end items-end flex">
                <img
                  src="/Login/Sign-in Icon 2.svg"
                  alt="icon 2"
                  className="flex text-end w-24 h-24 opacity-15"
                />
              </div>
              <img
                src="/Login/Sign-in Icon 3.svg"
                alt="icon 3"
                className="flex text-end w-20 h-20 -mt-6 opacity-10"
              />
            </div>
          </aside>

          <section className="login-panel">
            <div
              className={`login-container px-10 py-20 ${isAdminMode ? "admin-mode" : ""}`}
            >
              {/* <div className="top-nav">
                <Link to="/" className="back-link">
                  <i className="fas fa-arrow-left"></i> Back
                </Link>
              </div> */}

              <div className="flex flex-col gap-2 mb-5">
                <Link to="/">
                  <img
                    src="/Fluenesia.png"
                    alt="fluenesia icon"
                    className="w-32"
                  />
                </Link>
                <p>Selamat datang kembali!</p>
                <div className="w-11 h-1 bg-[#B70100]/20 rounded-full"></div>
              </div>

              <div className="border border-[#E9BCB5]/50 rounded-3xl">
                <form
                  onSubmit={handleLogin}
                  className="rounded-3xl border-4 border-[#FFF0EE]/30 p-8"
                >
                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <div className="input-wrapper">
                      <i className="fas fa-envelope input-icon"></i>
                      <input
                        type="email"
                        className="form-input"
                        placeholder="Input your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={isLoading}
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <div className="input-wrapper">
                      <i className="fas fa-lock input-icon"></i>
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-input"
                        placeholder="Input your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        autoComplete="off"
                      />
                      <i
                        className={`fas ${showPassword ? "fa-eye" : "fa-eye-slash"} toggle-pass`}
                        onClick={togglePassword}
                        style={{ pointerEvents: isLoading ? "none" : "auto" }}
                      ></i>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 actions-links mb-6 -mt-2">
                    <Link
                      to="/auth/forgot-password"
                      className="forgot-link"
                      style={{ pointerEvents: isLoading ? "none" : "auto" }}
                    >
                      Forgot Password?
                    </Link>
                    <label className="remember-box">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        disabled={isLoading}
                      />
                      Keep me signed in
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="btn-login"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="loading-spinner"></span>
                        Processing...
                      </>
                    ) : isAdminMode ? (
                      "Login as Admin"
                    ) : (
                      "Sign In to Dashboard"
                    )}
                    <ArrowRight className="w-5 h-auto" />
                  </button>

                  <div className="flex gap-2 w-full items-center mt-2 mb-8 px-8">
                    <div className="w-1/4 border-t border-[#E9BCB5]/50"></div>
                    <p className="login-desc-label font-medium">SECURE LOGIN</p>
                    <div className="w-1/4 border-t border-[#E9BCB5]/50"></div>
                  </div>

                  {!isAdminMode && (
                    <div className="social-grid">
                      <button
                        type="button"
                        className="btn-social google"
                        onClick={() => googleLogin()}
                        disabled={isLoading}
                      >
                        <FcGoogle /> Sign In with Google
                      </button>

                      {/* <FacebookLogin
                        appId={import.meta.env.VITE_FACEBOOK_APP_ID || ""}
                        autoLoad={false}
                        fields="name,email,picture"
                        callback={responseFacebook}
                        isDisabled={isLoading}
                        render={(renderProps: any) => (
                          <button
                            type="button"
                            className="btn-social facebook"
                            onClick={renderProps.onClick}
                            disabled={isLoading}
                          >
                            <SiFacebook /> Sign In with Facebook
                          </button>
                        )}
                      /> */}
                    </div>
                  )}
                </form>
              </div>

              <div className="footer-text">
                {isAdminMode ? (
                  <span>
                    Bukan Admin?
                    <button
                      onClick={() => setIsAdminMode(false)}
                      className="signup-link"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                      }}
                      disabled={isLoading}
                    >
                      Masuk sebagai User
                    </button>
                  </span>
                ) : (
                  <div className="flex flex-col sm:flex-row items-center justify-center w-full mt-6">
                    Don't have an account yet?
                    <Link
                      to="/auth/Register"
                      className="signup-link"
                      style={{ pointerEvents: isLoading ? "none" : "auto" }}
                    >
                      Join as Student
                      <ArrowRight className="w-5 h-auto" />
                    </Link>
                  </div>
                )}
              </div>

              {!isAdminMode && (
                <div className="admin-toggle">
                  <p className="text-xs w-2/3 font-medium text-[#5B5F63]/70">
                    © 2024 FLUENESIA EDUCATION GROUP. OFFICIAL LANGUAGE CENTER.
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Login;
